import { useState } from 'react';
import { SeizedAssetStatus, SeizedAssetType } from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';
import type { AddSeizedAssetValue } from './useLegalCaseSeizedAssets';

// Campos com binding próprio na tela; qualquer outro 422 do backend cai no bloco residual.
const INLINE_FIELDS = new Set([
    'assetType', 'status', 'description', 'grade', 'competingCreditor',
    'estimatedValue', 'appraisedValue', 'collateralAssetUuid',
    'plate', 'renavam', 'chassis', 'state',
    'registrationNumber', 'registryOffice', 'district',
    'serialNumber', 'model', 'harvest', 'volume', 'warehouse',
    'identification',
]);

export const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

// Quais campos de identificação aparecem por tipo de bem. Dirige o render (o form mostra só
// a família do tipo escolhido) e o envio (só esses campos vão no payload). FINANCIAL/OTHER
// se identificam pela descrição, sem campo estruturado.
export const IDENTIFICATION_FIELDS_BY_TYPE: Record<string, string[]> = {
    [SeizedAssetType.VEHICLE]: ['plate', 'renavam', 'chassis', 'state'],
    [SeizedAssetType.REAL_ESTATE]: ['registrationNumber', 'registryOffice', 'district'],
    [SeizedAssetType.MACHINERY]: ['serialNumber', 'model'],
    [SeizedAssetType.GRAINS]: ['harvest', 'volume', 'warehouse'],
    [SeizedAssetType.FINANCIAL]: [],
    [SeizedAssetType.OTHER]: [],
};

// UF é um select (mesmo padrão do cadastro do caso, LegalCaseForm), não texto livre.
export const UF_OPTIONS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO']
    .map((uf) => ({ value: uf, label: uf }));

// Rótulos pt-BR de cada campo de identificação, para o form montar os inputs dinamicamente.
export const IDENTIFICATION_FIELD_LABELS: Record<string, string> = {
    plate: 'Placa',
    renavam: 'RENAVAM',
    chassis: 'Chassi',
    state: 'UF',
    registrationNumber: 'Matrícula',
    registryOffice: 'Cartório (CRI)',
    district: 'Comarca',
    serialNumber: 'Nº de série',
    model: 'Modelo',
    harvest: 'Safra',
    volume: 'Volume',
    warehouse: 'Armazém',
};

const ALL_IDENTIFICATION_FIELDS = [
    'plate', 'renavam', 'chassis', 'state',
    'registrationNumber', 'registryOffice', 'district',
    'serialNumber', 'model', 'harvest', 'volume', 'warehouse',
];

interface UseLegalCaseSeizedAssetFormArgs {
    onSubmit: (value: AddSeizedAssetValue) => Promise<boolean>;
}

const emptyIdentification = (): Record<string, string> =>
    ALL_IDENTIFICATION_FIELDS.reduce((acc, name) => ({ ...acc, [name]: '' }), {});

const toGradeOrUndefined = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
};

// Valores monetários usam CurrencyInput (value numérico); 0 = "não informado" (o form começa
// zerado), então não é enviado — coerente com o "Valor da Causa" do cadastro do caso.
const moneyOrUndefined = (value: number): number | undefined =>
    value && value > 0 ? value : undefined;

// Estado e handlers do formulário de bem, fora do componente (diretriz do repo: sem função
// dentro de função). A identificação fica num record indexado por nome de campo — só os
// campos do tipo escolhido são enviados. Centraliza o reset ao enviar com sucesso.
export default function useLegalCaseSeizedAssetForm({ onSubmit }: UseLegalCaseSeizedAssetFormArgs) {
    const [assetType, setAssetType] = useState<SeizedAssetType>(SeizedAssetType.VEHICLE);
    const [status, setStatus] = useState<SeizedAssetStatus>(SeizedAssetStatus.LOCATED);
    const [description, setDescription] = useState('');
    const [grade, setGrade] = useState('');
    const [competingCreditor, setCompetingCreditor] = useState('');
    const [estimatedValue, setEstimatedValue] = useState(0);
    const [appraisedValue, setAppraisedValue] = useState(0);
    const [identification, setIdentification] = useState<Record<string, string>>(emptyIdentification());

    const setIdentificationField = (name: string, value: string) =>
        setIdentification((prev) => ({ ...prev, [name]: value }));

    const reset = () => {
        setAssetType(SeizedAssetType.VEHICLE);
        setStatus(SeizedAssetStatus.LOCATED);
        setDescription('');
        setGrade('');
        setCompetingCreditor('');
        setEstimatedValue(0);
        setAppraisedValue(0);
        setIdentification(emptyIdentification());
    };

    // Tipo e status têm default; o backend valida a identificação mínima (422). O botão fica
    // sempre habilitado — o feedback vem do backend, como nas demais seções.
    const canSubmit = true;

    const submit = async () => {
        const fields = IDENTIFICATION_FIELDS_BY_TYPE[assetType] || [];
        const identificationPayload = fields.reduce((acc, name) => {
            const value = identification[name]?.trim();
            return value ? { ...acc, [name]: value } : acc;
        }, {} as Record<string, string>);

        const ok = await onSubmit({
            assetType,
            status,
            description: description.trim() || undefined,
            grade: toGradeOrUndefined(grade),
            competingCreditor: competingCreditor.trim() || undefined,
            estimatedValue: moneyOrUndefined(estimatedValue),
            appraisedValue: moneyOrUndefined(appraisedValue),
            ...identificationPayload,
        });
        if (ok) reset();
    };

    return {
        assetType, setAssetType,
        status, setStatus,
        description, setDescription,
        grade, setGrade,
        competingCreditor, setCompetingCreditor,
        estimatedValue, setEstimatedValue,
        appraisedValue, setAppraisedValue,
        identification, setIdentificationField,
        canSubmit, submit, reset,
    };
}
